import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Info } from 'lucide-react';

// Mirrors backend/src/services/ticketQualityService.js — keep in sync.
const MIN = { subject: 10, description: 20, diagnosis: 30, solution: 80 };
const RICH_DIAG = 80;
const W = { subject: 15, description: 15, diagnosis: 20, solution: 25, time_logged: 15, category: 10 };

export function computeQuality({ subjectLen, firstInboundLen, diagnosisLen, solutionLen, timeMinutes, categoryId }) {
  const sLen = Number(subjectLen) || 0;
  const dLen = Number(diagnosisLen) || 0;
  const solLen = Number(solutionLen) || 0;
  const descLen = Number(firstInboundLen) || 0;

  const descUncontrollable = descLen < MIN.description;
  const descCompensated = descUncontrollable && dLen >= RICH_DIAG;
  const descOk = descLen >= MIN.description || descCompensated;

  const items = {
    subject:     { ok: sLen >= MIN.subject,      weight: W.subject,     current: sLen,   min: MIN.subject },
    description: { ok: descOk,                    weight: W.description, current: descLen, min: MIN.description, uncontrollable: descUncontrollable, compensated: descCompensated, richTarget: RICH_DIAG },
    diagnosis:   { ok: dLen >= MIN.diagnosis,    weight: W.diagnosis,   current: dLen,   min: MIN.diagnosis },
    solution:    { ok: solLen >= MIN.solution,   weight: W.solution,    current: solLen, min: MIN.solution },
    time_logged: { ok: Number(timeMinutes) > 0,  weight: W.time_logged, current: Number(timeMinutes) || 0 },
    category:    { ok: !!categoryId,              weight: W.category },
  };
  let score = 0;
  for (const k of Object.keys(items)) if (items[k].ok) score += items[k].weight;
  return { score, items };
}

function Row({ ok, label, right, muted }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
      borderBottom: '1px dashed var(--c-border)', fontSize: 11,
    }}>
      <span style={{
        width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: ok ? '#16a34a' : 'transparent',
        border: ok ? 'none' : '1px solid var(--c-border)',
      }}>
        {ok ? <Check size={9} strokeWidth={3} color="#fff" /> : null}
      </span>
      <span style={{ flex: 1, color: muted ? 'var(--c-text-muted)' : 'var(--c-text)', fontWeight: ok ? 500 : 400 }}>
        {label}
      </span>
      <span style={{ fontSize: 10, fontWeight: 600, color: ok ? '#16a34a' : 'var(--c-text-muted)' }}>
        {right}
      </span>
    </div>
  );
}

export default function TicketQualityPreview({
  subjectLen, firstInboundLen, diagnosisLen, solutionLen, timeMinutes, categoryId,
  compact = false,
}) {
  const { t } = useTranslation();
  const { score, items } = computeQuality({ subjectLen, firstInboundLen, diagnosisLen, solutionLen, timeMinutes, categoryId });
  const color = score >= 80 ? '#16a34a' : score >= 50 ? '#d97706' : '#dc2626';
  const bg = score >= 80 ? 'rgba(22,163,74,0.06)' : score >= 50 ? 'rgba(217,119,6,0.06)' : 'rgba(220,38,38,0.06)';
  const border = score >= 80 ? 'rgba(22,163,74,0.25)' : score >= 50 ? 'rgba(217,119,6,0.25)' : 'rgba(220,38,38,0.25)';

  // Hint: customer's first message was too short and the agent's diagnosis isn't yet long enough
  // to compensate. Tell them exactly how many more characters they need.
  const desc = items.description;
  const diag = items.diagnosis;
  const needsMore = desc.uncontrollable && !desc.compensated ? Math.max(0, RICH_DIAG - diag.current) : 0;

  return (
    <div style={{
      padding: compact ? 8 : 10, borderRadius: 8, marginBottom: 12,
      background: bg, border: `1px solid ${border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color, minWidth: 90 }}>
          {t('tickets.completeness.label')}: {score}%
        </div>
        <div style={{ flex: 1, height: 5, background: 'var(--c-border)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${score}%`, background: color, transition: 'width 0.2s' }} />
        </div>
      </div>

      <div>
        <Row ok={items.subject.ok}
          label={t('tickets.completeness.subject')}
          right={items.subject.ok ? `+${items.subject.weight}` : `${items.subject.current}/${items.subject.min}`} />

        <Row ok={items.description.ok}
          muted={items.description.uncontrollable}
          label={items.description.uncontrollable
            ? t('tickets.completeness.description_uncontrollable')
            : t('tickets.completeness.description')}
          right={items.description.compensated
            ? `+${items.description.weight} ✓`
            : items.description.ok
              ? `+${items.description.weight}`
              : `${items.description.current}/${items.description.min}`} />

        <Row ok={items.diagnosis.ok}
          label={t('tickets.completeness.diagnosis')}
          right={items.diagnosis.ok ? `+${items.diagnosis.weight}` : `${items.diagnosis.current}/${items.diagnosis.min}`} />

        <Row ok={items.solution.ok}
          label={t('tickets.completeness.solution')}
          right={items.solution.ok ? `+${items.solution.weight}` : `${items.solution.current}/${items.solution.min}`} />

        <Row ok={items.time_logged.ok}
          label={t('tickets.completeness.time_logged')}
          right={items.time_logged.ok
            ? `+${items.time_logged.weight}`
            : t('tickets.completeness.itemMissing')} />

        <Row ok={items.category.ok}
          label={t('tickets.completeness.category')}
          right={items.category.ok
            ? `+${items.category.weight}`
            : t('tickets.completeness.itemMissing')} />
      </div>

      {needsMore > 0 && (
        <div style={{
          marginTop: 8, padding: '6px 8px', borderRadius: 4,
          background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)',
          fontSize: 10, color: '#3b82f6', display: 'flex', alignItems: 'flex-start', gap: 6,
        }}>
          <Info size={12} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{t('tickets.completeness.richHint', { needed: needsMore })}</span>
        </div>
      )}
    </div>
  );
}
